import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Truck, Phone, KeyRound, ChevronLeft, Send, MessageCircle, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { configureStatusBar } from "../lib/statusBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { joinPhone, COUNTRY_CODES } from "../lib/phone";
import LegalFooter from "../components/LegalFooter";
import { resolveTenantSlug } from "../lib/tenant";

//: Shown instead of the login when this app cannot say which store it
//: belongs to. A rider exists in exactly one store, so guessing one would
//: only produce a misleading "not registered" error from the wrong store.
export const RIDER_APP_NOT_LINKED =
  "This Rider App is not linked to a store. Please open the Rider App link your store shared, or contact your admin.";

export default function DeliveryLogin() {
  const nav = useNavigate();
  const { login, user, loading: authLoading } = useAuth();

  // The native Rider app's WebView always starts on THIS exact route
  // (build_app.py bakes /delivery/login into its server.url — unlike the
  // customer app, which starts on a route that already works whether or
  // not a session exists). With no redirect-away-when-already-logged-in
  // check, a rider with a perfectly valid, persisted session — token,
  // refresh token, and a confirmed-working /api/auth/me — still landed
  // back on this login form every time they relaunched the app after a
  // full kill, and had to OTP in again despite never actually being
  // logged out. Mirrors the existing inverse guard in RiderToday.jsx
  // (redirects TO login when NOT authenticated); this is the same check
  // the other direction.
  useEffect(() => {
    if (!authLoading && user?.role === "delivery_boy") nav("/rider/today", { replace: true });
  }, [user, authLoading, nav]);
  // Rider login intentionally starts blank — no demo credentials are
  // displayed on the screen (per Apple/Play Store review feedback that
  // production builds should not surface test details).
  const [step, setStep] = useState("phone");
  const [cc, setCc] = useState("+91");
  const [local, setLocal] = useState("");
  const phone = joinPhone(cc, local);
  const [otp, setOtp] = useState("");
  const [channel, setChannel] = useState("sms");
  const [loading, setLoading] = useState(false);
  const [resendSecs, setResendSecs] = useState(0);
  // The store this Rider App belongs to, from the app's own context
  // (native start URL /t/<slug>/…, ?t=, the stored slug, or a tenant
  // subdomain) — resolved once, with NO default-store fallback, and sent
  // unchanged with both the OTP request and the OTP verification.
  const [tenantSlug] = useState(() => resolveTenantSlug());
  // "checking" | "ok" | "unverified" (network) | "missing" | "invalid"
  const [store, setStore] = useState({ status: tenantSlug ? "checking" : "missing", name: "" });
  useEffect(() => {
    if (!tenantSlug) return undefined;
    let cancelled = false;
    api.get(`/api/public/tenant/${encodeURIComponent(tenantSlug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const t = data?.tenant;
        setStore(t?.id ? { status: "ok", name: t.name || "" } : { status: "invalid", name: "" });
      })
      .catch((e) => {
        if (cancelled) return;
        // A store that does not exist is a configuration error; a network
        // problem is not — the server still refuses an unknown store.
        setStore({ status: e?.response?.status === 404 ? "invalid" : "unverified", name: "" });
      });
    return () => { cancelled = true; };
  }, [tenantSlug]);
  const configError = store.status === "missing" || store.status === "invalid" ? RIDER_APP_NOT_LINKED : "";

  useEffect(() => {
    if (resendSecs <= 0) return undefined;
    const id = setInterval(() => setResendSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendSecs]);

  const sendOtp = async (overrideChannel) => {
    if (configError) { toast.error(configError); return; }
    if (local.replace(/\D/g, "").length < 10) {
      toast.error("Enter a 10-digit phone number"); return;
    }
    const useChannel = overrideChannel || channel;
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/otp/request", {
        phone, role: "delivery_boy", channel: useChannel, tenant_slug: tenantSlug,
      });
      toast.success(data.message || `OTP sent via ${useChannel.toUpperCase()}`);
      setChannel(useChannel);
      setStep("otp");
      setResendSecs(data.resend_after || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const resendOtp = (resendChannel) => {
    if (resendSecs > 0) return;
    setOtp("");
    sendOtp(resendChannel);
  };

  const verifyOtp = async () => {
    if (configError) { toast.error(configError); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/otp/verify", {
        // The same store the OTP was requested for — never a default: a
        // rider looked up in the wrong store is "not registered" there.
        phone, otp, role: "delivery_boy", tenant_slug: tenantSlug,
      });
      login(data.token, data.user, data.tenant || null, data.refresh_token || null);
      toast.success("Welcome!");
      nav("/rider/today");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-safe bg-brand-ink text-white flex flex-col" data-testid="delivery-login-page">
      <div aria-hidden className="fixed top-0 left-0 right-0 z-40 bg-brand-ink pointer-events-none"
           style={{ height: "env(safe-area-inset-top, 0px)" }}/>
      <div className="p-5">
        <Link to="/" className="inline-flex items-center gap-1 text-white/70" data-testid="back-link">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-brand-clay items-center justify-center mb-4">
              <Truck className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-extrabold text-3xl tracking-tight">Rider App</div>
            <div className="text-sm text-white/60 uppercase tracking-wider font-bold mt-1">Rider Console</div>
            {store.name && (
              <div className="text-sm text-white/80 font-bold mt-2" data-testid="rider-store-name">{store.name}</div>
            )}
          </div>

          {configError && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-2xl p-4 mb-4 text-sm font-semibold"
                 role="alert" data-testid="rider-config-error">
              {configError}
            </div>
          )}

          <div className="bg-white text-brand-ink rounded-3xl p-7">
            {step === "phone" ? (
              <>
                <h2 className="font-heading text-2xl font-bold mb-6">Phone Login</h2>
                <div className="flex gap-2">
                  <div className="relative">
                    <select value={cc} onChange={(e) => setCc(e.target.value)}
                            className="appearance-none h-16 pl-3 pr-7 rounded-2xl border border-neutral-200 bg-white font-mono font-bold text-base cursor-pointer"
                            data-testid="delivery-cc"
                            aria-label="Country code">
                      {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      type="tel" inputMode="numeric" maxLength={10}
                      value={local}
                      onChange={(e) => setLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="h-16 pl-12 rounded-2xl text-lg font-mono tracking-wider"
                      placeholder="98765 00001" data-testid="phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button onClick={() => setChannel("sms")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "sms" ? "border-brand-clay bg-brand-clay/10 text-brand-clay" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid="channel-sms">
                    <Send className="w-4 h-4" /> SMS
                  </button>
                  <button onClick={() => setChannel("whatsapp")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "whatsapp" ? "border-brand-leaf bg-brand-leaf/10 text-brand-leaf" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid="channel-whatsapp">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                </div>
                <Button
                  onClick={() => sendOtp()} disabled={loading || !!configError || store.status === "checking"}
                  className="w-full mt-6 h-16 rounded-3xl bg-brand-clay hover:bg-orange-700 font-black text-lg btn-tactile uppercase tracking-wide"
                  data-testid="send-otp-btn"
                >
                  {loading ? "..." : "Send OTP"}
                </Button>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold mb-2">Enter OTP</h2>
                <p className="text-neutral-600 text-sm mb-6">Sent via <b className="capitalize">{channel}</b> to <b>{cc} {local}</b></p>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input
                    type="text" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)}
                    className="h-16 pl-12 rounded-2xl text-2xl font-mono tracking-[0.4em]"
                    data-testid="otp-input"
                  />
                </div>
                <Button
                  onClick={verifyOtp} disabled={loading || otp.length < 4}
                  className="w-full mt-6 h-16 rounded-3xl bg-brand-clay hover:bg-orange-700 font-black text-lg btn-tactile uppercase tracking-wide"
                  data-testid="verify-otp-btn"
                >
                  {loading ? "..." : "Start My Day"}
                </Button>

                <div className="mt-5 pt-4 border-t border-neutral-100" data-testid="resend-block">
                  {resendSecs > 0 ? (
                    <div className="text-center text-sm text-neutral-500" data-testid="resend-countdown">
                      Resend available in <span className="font-bold text-neutral-700">{resendSecs}s</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-center text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">
                        Didn&rsquo;t get it? Resend via
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => resendOtp("sms")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-clay text-brand-clay inline-flex items-center justify-center gap-2 hover:bg-brand-clay/10 disabled:opacity-50"
                                data-testid="resend-sms-btn">
                          <Send className="w-4 h-4" /> SMS
                        </button>
                        <button onClick={() => resendOtp("whatsapp")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-leaf text-brand-leaf inline-flex items-center justify-center gap-2 hover:bg-brand-leaf/10 disabled:opacity-50"
                                data-testid="resend-whatsapp-btn">
                          <MessageCircle className="w-4 h-4" /> WhatsApp
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <button onClick={() => { setStep("phone"); setResendSecs(0); }}
                        className="w-full mt-4 text-sm text-white/60 hover:text-white"
                        data-testid="change-phone-btn">
                  Change phone / channel
                </button>
              </>
            )}
          </div>
          {/* Footer in-column so it stacks below on mobile (not a sibling
              of the centered flex column). */}
          <LegalFooter theme="dark"/>
        </div>
      </div>
    </div>
  );
}
